import React, { useState, useEffect, useCallback } from 'react';
import { Users } from 'lucide-react';
import axios from 'axios';
import { API } from '../config';

const RATE = 30;

const durationToHours = (d) => {
  const s = (d || '').toLowerCase();
  if (s.includes('2 hour')) return 2;
  if (s.includes('1 hr 30') || s.includes('90')) return 1.5;
  if (s.includes('45')) return 0.75;
  if (s.includes('30 min')) return 0.5;
  return 1;
};

const weekRange = (offset) => {
  const now = new Date();
  const monday = new Date(now);
  monday.setDate(now.getDate() - ((now.getDay() + 6) % 7) + offset * 7);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return [fmt(monday), fmt(sunday)];
};

export const TherapistEarnings = () => {
  const [bookings, setBookings] = useState([]);
  const [week, setWeek] = useState(0);

  const fetchAll = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/bookings`);
      setBookings(res.data);
    } catch (err) {
      console.error('Failed to load bookings for earnings:', err);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const [start, end] = weekRange(week);
  const inWeek = bookings.filter(b => b.status !== 'cancelled' && b.booking_date >= start && b.booking_date <= end);
  const byTherapist = {};
  inWeek.forEach(b => {
    const tid = b.assigned_therapist || b.therapist_preference || 'no_preference';
    const e = byTherapist[tid] || { sessions: 0, hours: 0 };
    e.sessions += 1;
    e.hours += durationToHours(b.service_duration);
    byTherapist[tid] = e;
  });
  const rows = Object.entries(byTherapist).sort((a, b) => b[1].hours - a[1].hours);
  const totalHours = rows.reduce((s, [, e]) => s + e.hours, 0);

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm mb-6" data-testid="therapist-earnings">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Users size={18} className="text-stone-500" />
          <h2 className="font-semibold text-stone-800">Therapist Earnings</h2>
          <span className="text-xs text-stone-400">€{RATE}/hr · {start} → {end}</span>
        </div>
        <div className="flex gap-1">
          <button
            onClick={() => setWeek(-1)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border ${week === -1 ? 'bg-stone-800 text-white border-stone-800' : 'border-stone-200 text-stone-500 hover:border-stone-300'}`}
            data-testid="earnings-last-week-btn"
          >
            Last Week
          </button>
          <button
            onClick={() => setWeek(0)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border ${week === 0 ? 'bg-stone-800 text-white border-stone-800' : 'border-stone-200 text-stone-500 hover:border-stone-300'}`}
            data-testid="earnings-this-week-btn"
          >
            This Week
          </button>
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-stone-400 text-center py-4">No bookings this week</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-stone-400 uppercase tracking-wider border-b border-stone-100">
              <th className="py-2">Therapist</th>
              <th className="py-2 text-center">Sessions</th>
              <th className="py-2 text-center">Hours</th>
              <th className="py-2 text-right">Pay</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([tid, e]) => (
              <tr key={tid} className="border-b border-stone-50" data-testid={`earnings-row-${tid}`}>
                <td className="py-2 font-medium text-stone-700">
                  {tid === 'no_preference' ? <span className="text-stone-400">Unassigned / No preference</span> : tid.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                </td>
                <td className="py-2 text-center text-stone-600">{e.sessions}</td>
                <td className="py-2 text-center text-stone-600">{e.hours.toFixed(2)}h</td>
                <td className="py-2 text-right font-semibold text-stone-800">€{(e.hours * RATE).toFixed(2)}</td>
              </tr>
            ))}
            <tr>
              <td className="py-2 font-semibold text-stone-800">Total</td>
              <td className="py-2 text-center font-semibold text-stone-800">{inWeek.length}</td>
              <td className="py-2 text-center font-semibold text-stone-800">{totalHours.toFixed(2)}h</td>
              <td className="py-2 text-right font-bold text-stone-900" data-testid="earnings-total-pay">€{(totalHours * RATE).toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
};
